// Per-tile widget settings — single source of truth backed by localStorage.
//
// Each widget kind defines its own settings shape + defaults; this hook
// is the generic plumbing that:
//   - Reads / writes JSON under `weblogcat:settings:<tileId>:<kind>`
//   - Hydrates from `localStorage` on mount, falling back to `defaults`.
//   - Pushes mutations back to `localStorage` AND emits an in-memory
//     pub/sub event so other consumers of the same key (e.g. an open
//     `<WidgetSettingsModal/>`) re-render without waiting on the
//     cross-tab `storage` event.
//   - Recovers settings written under the older per-serial key scheme,
//     and folds even older pre-modal legacy keys (Logcat filters, Shell
//     cwd, Dumpsys preset) into the new shape.
//
// The "two surfaces, one state" contract lives here: any widget that
// uses this hook can render its on-bar controls AND the modal's
// matching controls, both reading / writing through the same setter.
//
// Decision: the storage key is GLOBAL per dashboard — keyed by tile id +
// widget kind, NOT by device serial. The dashboard *layout* is already
// global, so tying each tile's settings to a serial meant the same tiles
// came back on reconnect but their settings silently reset to defaults
// whenever the resolved serial differed — an empty WebUSB serial, or the
// same phone reporting a different serial over USB vs the Web Device
// Proxy. Global keying makes a tile's settings follow its (global) panel.
// The widget kind is part of the key so two widgets of different kinds on
// one tile id (impossible today, but cheap to guard against) can't collide.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAdb } from './adbContext';
import type { WidgetKind } from '../types';

// ---- Storage key helpers ---------------------------------------------------

/** Resolve the live key for a (tile, kind) pair. Global per dashboard —
 *  deliberately NOT namespaced by device serial (see the file header). */
export function settingsKey(tileId: string, kind: WidgetKind): string {
  return `weblogcat:settings:${tileId}:${kind}`;
}

// ---- In-memory pub/sub -----------------------------------------------------
//
// `localStorage` only emits `storage` events to *other* documents, not
// the writing one. We need the writing document to notify its own
// subscribers (modal + widget body share the same hook instance and the
// same key). The bus is keyed by the full storage key so unrelated
// settings don't wake each other up.

type Listener = () => void;
const listeners = new Map<string, Set<Listener>>();

function subscribe(key: string, fn: Listener): () => void {
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(fn);
  return () => {
    set!.delete(fn);
    if (set!.size === 0) listeners.delete(key);
  };
}

function notify(key: string): void {
  const set = listeners.get(key);
  if (!set) return;
  for (const fn of set) fn();
}

// ---- Migration registry ----------------------------------------------------
//
// Each entry knows how to fold a single legacy key into the new shape.
// On hydration, if the new key is unset and any registered legacy key
// has a value, we read it, fold it in, persist the new shape, and
// delete the legacy keys.
//
// Widgets register their migrations via `registerSettingsMigration` at
// module load — keeping the migration table close to the widget that
// owns the legacy key, so this file doesn't need to know about every
// widget's pre-modal storage layout.

export interface SettingsMigration<T> {
  /** Build the legacy key for this (serial, tileId) pair. */
  legacyKey: (serial: string, tileId: string) => string;
  /** Read the raw localStorage value and merge it into the partial settings. */
  apply: (raw: string, partial: Partial<T>) => Partial<T>;
}

const migrations = new Map<WidgetKind, Array<SettingsMigration<unknown>>>();

export function registerSettingsMigration<T>(
  kind: WidgetKind,
  migration: SettingsMigration<T>,
): void {
  const list = migrations.get(kind) ?? [];
  list.push(migration as SettingsMigration<unknown>);
  migrations.set(kind, list);
}

function applyMigrations<T>(
  kind: WidgetKind,
  serial: string,
  tileId: string,
  base: T,
): { merged: T; touched: boolean } {
  const list = migrations.get(kind);
  if (!list || list.length === 0) return { merged: base, touched: false };
  let touched = false;
  let acc: Partial<T> = {};
  for (const m of list) {
    const lk = m.legacyKey(serial, tileId);
    let raw: string | null;
    try {
      raw = localStorage.getItem(lk);
    } catch {
      raw = null;
    }
    if (raw == null) continue;
    try {
      acc = (m as SettingsMigration<T>).apply(raw, acc);
      touched = true;
    } catch {
      // Garbage in legacy storage shouldn't crash the widget.
    }
    try {
      localStorage.removeItem(lk);
    } catch {
      /* ignore */
    }
  }
  if (!touched) return { merged: base, touched: false };
  return { merged: { ...base, ...acc }, touched: true };
}

// ---- Hydration -------------------------------------------------------------

/**
 * Read settings for the global (tile, kind) key from `localStorage`. When
 * nothing is stored there yet, first recover any value written under the
 * older per-serial key scheme, then fall back to folding a pre-modal legacy
 * key (using `serial` to locate it).
 *
 * Exported for tests; widget code should reach for `useTileSettings` instead.
 */
export function hydrateTileSettings<T extends object>(
  kind: WidgetKind,
  serial: string,
  tileId: string,
  defaults: T,
): T {
  const key = settingsKey(tileId, kind);
  const fromNew = readSettings<T>(key, defaults);
  if (typeof localStorage === 'undefined') return fromNew;
  let rawNew: string | null = null;
  try {
    rawNew = localStorage.getItem(key);
  } catch {
    rawNew = null;
  }
  if (rawNew != null) return fromNew;

  // No value at the global key yet. Recover any settings written under the
  // older per-serial key scheme (`…:<serial>:<tileId>:<kind>`) and adopt
  // them into the global key — this is what brings a panel back after a
  // reconnect that resolved a different serial than the one it was saved
  // under, the bug global keying exists to prevent going forward.
  const recovered = recoverPerSerialSettings<T>(tileId, kind, defaults);
  if (recovered) {
    writeSettings(key, recovered);
    return recovered;
  }

  const { merged, touched } = applyMigrations<T>(kind, serial, tileId, defaults);
  if (touched) {
    writeSettings(key, merged);
    return merged;
  }
  return fromNew;
}

/**
 * Find settings stored under the legacy per-serial key scheme
 * (`weblogcat:settings:<serial>:<tileId>:<kind>`) for this tile + kind and
 * return the best candidate merged onto `defaults`, or null if none exist.
 *
 * Real-device buckets win over the simulator's `sim` bucket; ties break
 * lexically for determinism. Old buckets are left in place (harmless — this
 * runs only when the global key is unset, so it won't re-run once adopted).
 */
function recoverPerSerialSettings<T extends object>(
  tileId: string,
  kind: WidgetKind,
  defaults: T,
): T | null {
  if (typeof localStorage === 'undefined') return null;
  const prefix = 'weblogcat:settings:';
  const suffix = `:${tileId}:${kind}`;
  const globalKey = settingsKey(tileId, kind);
  const matches: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || k === globalKey) continue;
      // A per-serial key carries an extra `<serial>:` segment, so it is
      // strictly longer than the global key while sharing the suffix.
      if (k.startsWith(prefix) && k.endsWith(suffix) && k.length > globalKey.length) {
        matches.push(k);
      }
    }
  } catch {
    return null;
  }
  if (matches.length === 0) return null;
  const serialOf = (k: string) => k.slice(prefix.length, k.length - suffix.length);
  matches.sort((a, b) => {
    const rank = (serialOf(a) === 'sim' ? 1 : 0) - (serialOf(b) === 'sim' ? 1 : 0);
    return rank || (a < b ? -1 : a > b ? 1 : 0);
  });
  for (const k of matches) {
    let raw: string | null;
    try {
      raw = localStorage.getItem(k);
    } catch {
      continue;
    }
    if (raw == null) continue;
    try {
      const parsed = JSON.parse(raw) as Partial<T>;
      if (parsed && typeof parsed === 'object') return { ...defaults, ...parsed };
    } catch {
      /* skip a malformed bucket and try the next */
    }
  }
  return null;
}

function readSettings<T>(key: string, defaults: T): T {
  if (typeof localStorage === 'undefined') return defaults;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<T>;
    if (parsed == null || typeof parsed !== 'object') return defaults;
    return { ...defaults, ...parsed };
  } catch {
    return defaults;
  }
}

function writeSettings<T>(key: string, value: T): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore quota / privacy mode
  }
}

// ---- Hook ------------------------------------------------------------------

/**
 * Persisted, shared per-tile widget settings.
 *
 * `defaults` MUST be a stable reference — pass a module-level object,
 * not an inline literal, otherwise rehydration runs on every render
 * and partial merges thrash. Widgets should declare their defaults at
 * module scope alongside their settings type.
 */
export function useTileSettings<T extends object>(
  tileId: string,
  kind: WidgetKind,
  defaults: T,
): readonly [T, (patch: Partial<T>) => void] {
  const { device } = useAdb();
  // The key itself is serial-independent; `serial` only locates a pre-modal
  // legacy key during hydration. The simulator path has no serial, so it
  // falls back to the "sim" bucket for that legacy lookup.
  const serial = device?.serial ?? 'sim';
  const key = settingsKey(tileId, kind);

  // Defaults are captured by ref so the setter can read them without
  // closing over a freshly-built object every render.
  const defaultsRef = useRef(defaults);

  // Hydrate (with one-shot migration) on first render of this key.
  const [state, setState] = useState<T>(() =>
    hydrateTileSettings(kind, serial, tileId, defaults),
  );

  // Mirror of the committed state, kept current every render. `set` reads
  // this instead of relying on a functional updater so the persist + notify
  // side-effects run synchronously and unconditionally — a functional
  // updater is dropped when the owning component unmounts in the same tick
  // (e.g. the builder modal's "Save" closes itself), which silently lost
  // writes.
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  });

  // Re-hydrate if the storage key ever changes (only on a tileId/kind
  // change now that the key is serial-independent — device swaps no longer
  // touch it, which is the whole point: settings persist across reconnect).
  const lastKeyRef = useRef(key);
  useEffect(() => {
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;
    const fresh = readSettings<T>(key, defaultsRef.current);
    setState(fresh);
  }, [key]);

  // Subscribe to in-memory bus so co-mounted consumers (modal + widget)
  // see each other's writes immediately.
  useEffect(() => {
    return subscribe(key, () => {
      setState(readSettings<T>(key, defaultsRef.current));
    });
  }, [key]);

  // Cross-tab updates also wake us up. Guard the parse so other apps'
  // localStorage events don't crash the listener.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key) return;
      setState(readSettings<T>(key, defaultsRef.current));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key]);

  const set = useCallback(
    (patch: Partial<T>) => {
      const next = { ...stateRef.current, ...patch };
      // Pre-update the ref so a burst of `set` calls in one tick chains
      // off each other's result rather than the last committed render.
      stateRef.current = next;
      writeSettings(key, next);
      notify(key);
      setState(next);
    },
    [key],
  );

  return [state, set] as const;
}
